import { formatUrl } from 'url-lib';
import { initialize } from 'redux-form';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from '@eb/social-login-facebook';
import { getWindowObject, HAS_WINDOW } from '@eb/feature-detection';
import {
    LOOK_UP_FORM,
    SOCIAL_SIGN_UP_FORM,
    BASE_URL,
    PASSWORD_SET_DESTINATION,
    PASSWORD_SET_TOKEN,
    PASSWORD_MUST_LINK_ACCOUNT,
    PASSWORD_EXTERNAL_PROVIDER,
    INVALID_RESET_TOKEN,
    SET_PASSWORD_FORM,
    RESET_PASSWORD_PATH,
    CHECKOUT_URL,
} from '../constants';
import {
    GA_SET_PASSWORD_EVENT,
    GA_RESET_PASSWORD_EVENT,
} from '../constants/analytics';
import { getEmailFromState } from '../actions/auth';
import { trackTokenExpired } from '../actions/gaTracking';

export const normalizePath = (path) => `/${path.replace(/\//g, '')}`;

/*
    This function acts as a gateway to ensure that the user has had their
    email status checked before accessing /login or /signup forms specifically.

    They can access these potentially with corrupted email states if navigating
    back and forward with the browser while modifying the email held
    within the form.
*/
export const checkAccountStatus = ({ getState }, replace) => {
    const { authentication: { isAccountExistent } = {} } = getState();

    /*
     * If email does not exist within the store redirect to start of the
     * signin flow (they arrived via direct route or refresh)
     */
    if (!isAccountExistent) {
        replace(BASE_URL);
    }
};

export const checkEmailExists = ({ getState }, replace) => {
    const email = getEmailFromState(getState);

    /*
     * If email was not provided, return to login page.
     * It does not check whether the e-mail is confirmed or not
     * Only that it's provided
     */
    if (!email) {
        replace(BASE_URL);
    }
};

/**
 * Every time we return to /signin we want to re-initialize the form
 * so that error states are cleared etc.
 */
export const setUpForm = ({ dispatch }) => {
    dispatch(initialize(LOOK_UP_FORM));
};

export const setUpSocialForm = ({ getState, dispatch }, replace) => {
    const {
        social: { socialData, socialProvider },
    } = getState();

    if (socialData[socialProvider]) {
        const { firstName, lastName, email } = socialData[socialProvider];

        // If there's no data, go back to the first step in the flow
        if (!email && !firstName && !lastName) {
            replace(BASE_URL);
        }

        dispatch(
            initialize(SOCIAL_SIGN_UP_FORM, { email, firstName, lastName }),
        );
    } else {
        replace(BASE_URL);
    }
};

export const checkPasswordReset = ({ getState, dispatch }, query, replace) => {
    const sourceProvider = query['external_provider'];
    if (has(query, INVALID_RESET_TOKEN)) {
        const state = getState();
        const currentRoute = state.routing.locationBeforeTransitions.pathname;
        const setPasswordEvent = getSetPasswordEvent(currentRoute);
        trackTokenExpired({
            state: getState(),
            sourceProvider,
            path: setPasswordEvent,
        });
        replace(
            formatUrl(BASE_URL, {
                [INVALID_RESET_TOKEN]: 1,
            }),
        );
    } else if (has(query, PASSWORD_SET_TOKEN)) {
        dispatch(
            initialize(SET_PASSWORD_FORM, {
                [PASSWORD_SET_TOKEN]: query[PASSWORD_SET_TOKEN],
                [PASSWORD_MUST_LINK_ACCOUNT]: query[PASSWORD_MUST_LINK_ACCOUNT],
                [PASSWORD_SET_DESTINATION]: query[PASSWORD_SET_DESTINATION],
                [PASSWORD_EXTERNAL_PROVIDER]: query[PASSWORD_EXTERNAL_PROVIDER],
            }),
        );
    } else if (has(query, 'email')) {
        dispatch(initialize(LOOK_UP_FORM, { email: query.email }));
    }
};

export const isLoginProviderEnabled = (socialSSOSettings, provider) =>
    !isEmpty(socialSSOSettings) && !isEmpty(socialSSOSettings[provider]);

/*********************** GA TRACKING UTILS ***********************/
//Helper to determine if action should be ResetPassword or SetPassword
//The only determining factor is the current route as the same form
//is rendered in either case.
export const getSetPasswordEvent = (currentRoute) => {
    let event = GA_SET_PASSWORD_EVENT;

    if (currentRoute.indexOf(RESET_PASSWORD_PATH) > -1) {
        event = GA_RESET_PASSWORD_EVENT;
    }

    return event;
};

export const notifyParent = (messageName, additionalData) => {
    const parent = window.opener ? window.opener : window.parent;

    if (window.location.origin === parent.location.origin) {
        parent.postMessage(
            { ...additionalData, messageName },
            parent.location.origin,
        );
    }
};

export const isInitiatedByCheckout = () => {
    // This returns true if the authentication app has been opened by Checkout either through an iframe or via a pop-up window
    // Check for the SSR
    if (!HAS_WINDOW) return false;

    // Pop-up window case
    const windowOpener = getWindowObject('opener');
    // Iframe case
    const windowParent = getWindowObject('parent');

    const parentsLocation = windowOpener
        ? windowOpener.location
        : windowParent.location;

    if (!parentsLocation) {
        return false;
    }

    const expectedCheckoutLocationStart = `${window.location.origin}${CHECKOUT_URL}`;
    try {
        // Accessing href can generate a cross-domain origin error.
        // A typical example would be logging in through OAuth.
        return parentsLocation.href.startsWith(expectedCheckoutLocationStart);
    } catch (e) {
        // If an exception is caugth that means that we hit the cross-domain origin error
        // and therefore that checkout is not the initiator of this request.
        return false;
    }
};

//Generic function to check if user has Social Account
export const hasSocialAccount = ({
    socialSSOSettings,
    signInMethods,
    socialToCompare,
    useLookupSigninMethodSyntax = false,
}) => {
    if (
        signInMethods &&
        socialToCompare &&
        !isUndefined(signInMethods) &&
        !isUndefined(socialToCompare)
    ) {
        const lookupSigninMethodSyntax = useLookupSigninMethodSyntax
            ? '_login'
            : '';
        var signInMethodsToLower = signInMethods.map((v) => v.toLowerCase());
        var socialToCompareToLower = socialToCompare.map((v) =>
            v.toLowerCase(),
        );
        let result = true;
        socialToCompareToLower.forEach((methodTC) => {
            // Here we check if socialMethod is enabled and it's included in user login methods.
            const checkSocialEnabledAndIncluded =
                (isLoginProviderEnabled(socialSSOSettings, methodTC) &&
                    !signInMethodsToLower.includes(
                        methodTC + lookupSigninMethodSyntax,
                    )) ||
                !isLoginProviderEnabled(socialSSOSettings, methodTC);

            if (result && checkSocialEnabledAndIncluded) {
                result = false;
            }
        });
        return result;
    } else {
        return false;
    }
};
